import { useState } from "react";
import { useUser } from "@/queries/user/useUser";
import {
  updateUserProfileInputSchema,
  useUpdateUserProfile,
  addressSchema,
  phoneNumbersSchema,
} from "@/queries/user/useUpdateUserProfile";
import {
  AddressType,
  PhoneNumberType,
  Owner,
  OwnerType,
} from "@/api/SolarCloudApi";
import { toast } from "sonner";
import { FormCard2 } from "@/components/FormCard2";
import {
  findDefaultAddress,
  findDefaultPhoneNumber,
  updateAddresses,
  updatePhoneNumbers,
} from "@/util/user";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { CustomPhoneInput } from "@/components/ui/CustomPhoneInput";
import { AddressForm } from "@/components/AddressForm";
import OwnerDetails from "./OwnerDetails";
import { useCountries } from "@/hooks/Countries";

const basicNameSchema = updateUserProfileInputSchema.pick({
  givenName: true,
  familyName: true,
});

const userProfileUpdateSchema = basicNameSchema.extend({
  phoneNumber: phoneNumbersSchema,
  address: addressSchema,
});

type TUserProfileUpdate = z.infer<typeof userProfileUpdateSchema>;

export default function ProfileForm() {
  const [isEditing, setIsEditing] = useState(false);
  const { data: user } = useUser();
  const { countryFromCode } = useCountries();

  const defaultPhoneNumber = findDefaultPhoneNumber(user);
  const defaultAddress = findDefaultAddress(user);

  let countryVal = defaultAddress?.country ?? "";
  if (countryVal && countryVal.length === 2) {
    const c = countryFromCode(countryVal);
    countryVal = c?.name ?? "Australia";
  }

  const defaultValues = {
    givenName: user?.givenName ?? "",
    familyName: user?.familyName ?? "",
    phoneNumber: {
      number: defaultPhoneNumber?.number ?? "",
      type: defaultPhoneNumber?.type ?? PhoneNumberType.DEFAULT,
      uuid: defaultPhoneNumber?.uuid,
    },
    address: {
      country: countryVal,
      line1: defaultAddress?.line1 ?? "",
      line2: defaultAddress?.line2 ?? "",
      line3: defaultAddress?.line3 ?? "",
      line4: defaultAddress?.line4 ?? "",
      city: defaultAddress?.city ?? "",
      state: defaultAddress?.state ?? "",
      postcode: defaultAddress?.postcode ?? "",
      type: defaultAddress?.type ?? AddressType.HOME,
      uuid: defaultAddress?.uuid,
    },
  };

  const updateUserProfile = useUpdateUserProfile({
    mutationConfig: {
      onSuccess: () => {
        toast.success("Profile updated successfully");
        setIsEditing(false);
      },
      onError: (error: any) => {
        const message =
          error?.body?.message || error?.message || "Failed to update profile";
        toast.error(message);
      },
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  // Create owner object from current user
  const owner: Owner | undefined = user
    ? {
        type: OwnerType.USER,
        user: user,
      }
    : undefined;

  const onSubmit = (data: TUserProfileUpdate) => {
    const normalizedPhoneNumber = {
      ...data.phoneNumber,
      uuid: data.phoneNumber?.uuid ?? defaultPhoneNumber?.uuid,
      type:
        data.phoneNumber?.type ??
        defaultPhoneNumber?.type ??
        PhoneNumberType.DEFAULT,
    };

    const normalizedAddress = {
      ...data.address,
      uuid: data.address?.uuid ?? defaultAddress?.uuid,
      type:
        data.address?.uuid
          ? data.address?.type
          : defaultAddress?.type ?? data.address?.type ?? AddressType.HOME,
    };

    const userNumbers = updatePhoneNumbers(user, normalizedPhoneNumber as any);
    const addresses = updateAddresses(user, normalizedAddress as any);

    const updatedUser = {
      givenName: data.givenName,
      familyName: data.familyName,
      phoneNumbers: userNumbers,
      addresses: addresses,
    };

    const parseResult = updateUserProfileInputSchema.safeParse(updatedUser);
    if (!parseResult.success) {
      const firstError = parseResult.error.errors[0]?.message || "Invalid profile data";
      toast.error(firstError);
      return;
    }
    updateUserProfile.mutate(parseResult.data);
  };
  return (
    <FormCard2
      key={`${user?.uuid}-${isEditing}`}
      schema={userProfileUpdateSchema}
      options={{
        defaultValues,
      }}
      onSubmit={onSubmit}
      title="Profile Details"
      description="Update your profile information."
      className="max-w-3xl"
      cardClassName="bg-transparent"
    >
      {(form) => (
        <>
          <p className="text-sm">
            The below information will appear on the receipt. As many of our
            customers buy these as a gift the owners details (who you are
            gifting them to will need to be entered below)
          </p>

          <div className="border-b w-full border-brand-neutral-700" />

          {!isEditing && (
            <div className="w-full flex gap-6 items-start max-w-xl mx-auto">
              <OwnerDetails owner={owner} className="flex-1" />
              <div className="flex justify-between items-center">
                <Button onClick={handleEdit}>Edit</Button>
              </div>
            </div>
          )}

          {isEditing && (
            <div className="max-w-xl mx-auto space-y-6">
              <input
                type="hidden"
                {...form.register("phoneNumber.uuid")}
              />
              <input
                type="hidden"
                {...form.register("phoneNumber.type")}
              />
              <input
                type="hidden"
                {...form.register("address.uuid")}
              />
              <input
                type="hidden"
                {...form.register("address.type")}
              />

              <FormField
                control={form.control}
                name="givenName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder=""
                        {...field}
                        className="bg-brand-neutral-800"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="familyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder=""
                        {...field}
                        className="bg-brand-neutral-800"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber.number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <CustomPhoneInput
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <AddressForm
                form={form}
                name="address"
                placeholder="Search for your address"
              />

              <div className="flex justify-end items-center gap-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={updateUserProfile.isPending}
                  className="px-10"
                >
                  Cancel
                </Button>
                <Button
                  disabled={
                    updateUserProfile.isPending || !form.formState.isDirty
                  }
                  type="submit"
                  className=""
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
          <div className="border-b w-full border-brand-neutral-700" />
        </>
      )}
    </FormCard2>
  );
}
